import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, GraduationCap, RotateCcw, CheckCircle2, Circle,
  Eye, Ear, Sparkles, Users, Compass, BookOpen, Quote,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import { previewStore } from "./previewStore";

const API = process.env.REACT_APP_BACKEND_URL;

const JOURNEY_META = {
  SEE:   { code: "SEE",   label: "See the Person",         icon: Eye },
  HEAR:  { code: "HEAR",  label: "Hear the Voice",         icon: Ear },
  HONOR: { code: "HONOR", label: "Honor What Matters",     icon: Sparkles },
  STAY:  { code: "STAY",  label: "Stay Present",           icon: Users },
  CARRY: { code: "CARRY", label: "Carry the Voice Forward", icon: Compass },
};

const RHYTHM = [
  "HEAR THE VOICE",
  "LOOK BENEATH THE SURFACE",
  "LEARN THE PRINCIPLE",
  "PRACTICE THE CONVERSATION",
  "CARRY IT INTO CARE",
];

// ----- helpers -----
const isSessionComplete = (session, completions) =>
  !!completions[session.id];

const overallProgress = (course, completions) => {
  if (!course?.sessions?.length) return 0;
  const done = course.sessions.filter(s => completions[s.id]).length;
  return Math.round((done / course.sessions.length) * 100);
};

// ============================================================================
// Main component
// ============================================================================
export default function PortalPvcLearn() {
  const navigate = useNavigate();
  usePageMeta({
    title: "PVC-001 · Learner Preview | CCCHAPS Academy",
    description: "The Patient's Voice in Care — admin learner preview.",
    noindex: true,
  });

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // phase: orientation | dashboard | session | check | check-result | session-complete | transition | final-journey | before-finish | complete
  const [phase, setPhase] = useState("orientation");
  const [sessionIndex, setSessionIndex] = useState(0);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [checkIndex, setCheckIndex] = useState(0);
  const [checkAnswers, setCheckAnswers] = useState({});   // {qid: {selected, correct}}
  const [reflectionCache, setReflectionCache] = useState({}); // {inputId: text}
  const [progressTick, setProgressTick] = useState(0);   // forces re-render on save

  // Load PVC-001 via admin-preview endpoint (uses admin JWT cookie) OR the
  // learner-entitled endpoint (Bearer token from localStorage).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const learnerToken = (typeof window !== "undefined") ? localStorage.getItem("ccchaps.pvc.token") : null;
      try {
        // Try admin cookie first — no-op for learners
        const res = await axios.get(`${API}/api/admin/portal/preview/course?code=PVC-001`, { withCredentials: true });
        if (cancelled) return;
        setCourse(res.data.course);
        previewStore.enter("PVC-001");
        const state = previewStore.getState();
        setReflectionCache(state.progress.case_study_responses || {});
      } catch (adminErr) {
        // Fall back to learner-entitled path
        if (!learnerToken) {
          if (!cancelled) setLoadError("Please complete enrollment or sign in to access this course.");
          if (!cancelled) setLoading(false);
          return;
        }
        try {
          const res = await axios.get(`${API}/api/portal/pvc/course`, { headers: { Authorization: `Bearer ${learnerToken}` } });
          if (cancelled) return;
          setCourse(res.data.course);
          previewStore.enter("PVC-001");
          const state = previewStore.getState();
          setReflectionCache(state.progress.case_study_responses || {});
        } catch (learnerErr) {
          if (!cancelled) setLoadError(learnerErr?.response?.data?.detail || "No active enrollment. Please complete enrollment first.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const completions = previewStore.getState().progress.section_completions || {};
  void progressTick;

  const currentSession = course?.sessions?.[sessionIndex];
  const currentSection = currentSession?.sections?.[sectionIndex];
  const currentCheckQ = currentSession?.knowledge_check?.questions?.[checkIndex];

  // ---- actions ----
  const resetPreview = () => {
    if (!window.confirm("Reset PVC-001 preview progress? This clears all preview state (reflections, section completions, answers) — production learner data is not affected.")) return;
    previewStore.reset();
    setCheckAnswers({});
    setReflectionCache({});
    setSessionIndex(0);
    setSectionIndex(0);
    setCheckIndex(0);
    setPhase("orientation");
    setProgressTick(t => t + 1);
  };

  const goDashboard = () => { setPhase("dashboard"); };

  const openSession = (idx) => {
    setSessionIndex(idx);
    setSectionIndex(0);
    setCheckIndex(0);
    setCheckAnswers({});
    setPhase("session");
  };

  const advanceSection = () => {
    if (!currentSession) return;
    const totalSections = currentSession.sections.length;
    if (sectionIndex + 1 < totalSections) {
      setSectionIndex(i => i + 1);
    } else {
      // End of teaching sections — go to knowledge check
      setCheckIndex(0);
      setCheckAnswers({});
      setPhase("check");
    }
  };

  const retreatSection = () => {
    if (sectionIndex > 0) setSectionIndex(i => i - 1);
    else setPhase("dashboard");
  };

  const submitCheckAnswer = (qid, selected, correctKey) => {
    setCheckAnswers(prev => ({ ...prev, [qid]: { selected, correct: selected === correctKey } }));
    setPhase("check-result");
  };

  const nextCheckQuestion = () => {
    const qs = currentSession.knowledge_check.questions;
    if (checkIndex + 1 < qs.length) {
      setCheckIndex(i => i + 1);
      setPhase("check");
    } else {
      // Session complete
      previewStore.markSectionComplete(currentSession.id);
      // Record quiz snapshot per session (last one wins in previewStore.latest_quiz)
      const total = qs.length;
      const score = qs.reduce((n, q) => n + (checkAnswers[q.id]?.correct ? 1 : 0), 0);
      previewStore.recordQuizAttempt({ score, total, passed: score >= Math.ceil(total * 0.66), review: {} });
      setProgressTick(t => t + 1);
      setPhase("session-complete");
    }
  };

  const proceedFromSessionComplete = () => {
    if (currentSession.transition_next) {
      setPhase("transition");
    } else {
      // Final session — go to journey → before-finish → complete
      setPhase("final-journey");
    }
  };

  const beginNextSession = () => {
    if (sessionIndex + 1 < course.sessions.length) {
      openSession(sessionIndex + 1);
    } else {
      setPhase("final-journey");
    }
  };

  const saveReflection = (inputId, text) => {
    if (!inputId) return;
    previewStore.setReflection(inputId, text);
    setReflectionCache(prev => ({ ...prev, [inputId]: text }));
  };

  // ---- render ----
  if (loading) return <FullscreenMessage title="Loading preview…" />;
  if (loadError) return <FullscreenMessage title="Preview unavailable" body={loadError} action={<Link to="/care-portal/admin/academy" className="text-[#4A6FA5] font-semibold underline">Return to Academy admin</Link>} />;
  if (!course) return null;

  const progressPct = overallProgress(course, completions);
  const sessionsCompleted = course.sessions.filter(s => completions[s.id]).length;

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-pvc-learn">
      <PreviewChrome
        onReset={resetPreview}
        onDashboard={() => setPhase("dashboard")}
        progressPct={progressPct}
        sessionsCompleted={sessionsCompleted}
        totalSessions={course.sessions.length}
      />

      {phase === "orientation" && (
        <Orientation course={course} onContinue={() => setPhase("dashboard")} />
      )}

      {phase === "dashboard" && (
        <Dashboard
          course={course}
          completions={completions}
          onOpenSession={openSession}
          onFinish={() => setPhase("final-journey")}
          allDone={sessionsCompleted === course.sessions.length}
        />
      )}

      {phase === "session" && currentSection && (
        <SessionScreen
          session={currentSession}
          sectionIndex={sectionIndex}
          section={currentSection}
          totalSections={currentSession.sections.length + currentSession.knowledge_check.questions.length}
          onNext={advanceSection}
          onPrev={retreatSection}
          reflectionCache={reflectionCache}
          saveReflection={saveReflection}
        />
      )}

      {phase === "check" && currentCheckQ && (
        <CheckScreen
          session={currentSession}
          question={currentCheckQ}
          index={checkIndex}
          total={currentSession.knowledge_check.questions.length}
          onSubmit={(sel) => submitCheckAnswer(currentCheckQ.id, sel, currentCheckQ.answer)}
          onPrev={() => {
            if (checkIndex > 0) setCheckIndex(i => i - 1);
            else { setSectionIndex(currentSession.sections.length - 1); setPhase("session"); }
          }}
          instructions={currentSession.knowledge_check.instructions}
        />
      )}

      {phase === "check-result" && currentCheckQ && (
        <CheckResultScreen
          question={currentCheckQ}
          result={checkAnswers[currentCheckQ.id]}
          index={checkIndex}
          total={currentSession.knowledge_check.questions.length}
          onNext={nextCheckQuestion}
        />
      )}

      {phase === "session-complete" && (
        <SessionCompleteScreen
          session={currentSession}
          onContinue={proceedFromSessionComplete}
        />
      )}

      {phase === "transition" && (
        <TransitionScreen
          from={currentSession}
          onBegin={beginNextSession}
        />
      )}

      {phase === "final-journey" && (
        <FinalJourneyScreen
          course={course}
          onNext={() => setPhase("before-finish")}
        />
      )}

      {phase === "before-finish" && (
        <BeforeYouFinishScreen
          commitment={reflectionCache["pvc-commitment"] || ""}
          followup={reflectionCache["pvc-commitment-followup"] || ""}
          onFinish={() => setPhase("complete")}
        />
      )}

      {phase === "complete" && (
        <CourseCompleteScreen
          course={course}
          onDashboard={() => setPhase("dashboard")}
          onReset={resetPreview}
        />
      )}
    </div>
  );
}

// ============================================================================
// Preview chrome — persistent banner + reset button
// ============================================================================
function PreviewChrome({ onReset, onDashboard, progressPct, sessionsCompleted, totalSessions }) {
  return (
    <div className="sticky top-0 z-30 bg-amber-50 border-b border-amber-200" data-testid="pvc-preview-chrome">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-900">
            Admin Learner Preview
          </span>
          <span className="text-xs text-amber-900 font-medium hidden sm:inline">
            PVC-001 · Progress does not affect production data
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.14em] text-amber-900 font-semibold">
            {sessionsCompleted} / {totalSessions} Sessions · {progressPct}%
          </span>
          <button
            type="button"
            onClick={onDashboard}
            data-testid="pvc-preview-dashboard-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 font-medium"
          >
            Dashboard
          </button>
          <button
            type="button"
            onClick={onReset}
            data-testid="pvc-preview-reset-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 font-medium inline-flex items-center gap-1.5"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset Preview Progress
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Orientation — Before You Begin
// ============================================================================
function Orientation({ course, onContinue }) {
  const byb = course.before_you_begin || {};
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="pvc-orientation">
      <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-3">Before You Begin · Orientation</p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-slate-900 leading-tight">
        {course.title}
      </h1>
      <p className="mt-3 font-heading text-2xl text-slate-700 leading-tight">{course.subtitle}</p>

      <div className="mt-10 space-y-8">
        <Card title="Who this course is for" testid="pvc-orient-audience">
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-slate-700 text-sm">
            {(byb.who_this_is_for || []).map(a => (
              <li key={a} className="flex items-start gap-2"><Circle className="w-2 h-2 mt-2 fill-[#4A6FA5] text-[#4A6FA5] shrink-0" /> <span>{a}</span></li>
            ))}
          </ul>
        </Card>

        <Card title="What this course is — and is not" testid="pvc-orient-what">
          <p className="text-slate-700 leading-relaxed">{byb.what_this_is}</p>
          <blockquote className="mt-4 border-l-2 border-[#4A6FA5] pl-4 text-slate-800 italic">
            {byb.role_promise}
          </blockquote>
        </Card>

        <Card title="Primary text" testid="pvc-orient-book">
          <p className="text-slate-700 leading-relaxed flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
            <span>{byb.primary_text}</span>
          </p>
        </Card>

        <Card title="Educational disclaimer" testid="pvc-orient-disclaimer" tone="muted">
          <p className="text-slate-600 text-sm leading-relaxed">{byb.educational_disclaimer}</p>
        </Card>
      </div>

      <div className="mt-10 flex justify-end">
        <button
          type="button"
          onClick={onContinue}
          data-testid="pvc-orient-continue"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
        >
          Continue to Course <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

// ============================================================================
// Dashboard — 5-session grid
// ============================================================================
function Dashboard({ course, completions, onOpenSession, onFinish, allDone }) {
  const progressPct = overallProgress(course, completions);
  const done = course.sessions.filter(s => completions[s.id]).length;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="pvc-dashboard">
      <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-3">CCCHAPS Academy · PVC-001</p>
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">The Patient&rsquo;s Voice in Care</h1>
      <p className="mt-2 text-slate-600">SEE → HEAR → HONOR → STAY → CARRY</p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="pvc-dashboard-progress">
        <div className="flex items-center justify-between text-sm">
          <p className="font-semibold text-slate-800">Course Progress</p>
          <p className="text-slate-600">{done} of {course.sessions.length} Sessions Completed · {progressPct}%</p>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-[#4A6FA5]" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="pvc-session-grid">
        {course.sessions.map((s, i) => {
          const isDone = !!completions[s.id];
          const meta = JOURNEY_META[s.code] || { icon: BookOpen };
          const Icon = meta.icon;
          const status = isDone ? "Completed" : (done > i ? "In Progress" : (i === 0 || completions[course.sessions[i - 1]?.id]) ? "Ready" : "Locked");
          const canOpen = status !== "Locked";
          return (
            <button
              key={s.id}
              type="button"
              disabled={!canOpen}
              onClick={() => canOpen && onOpenSession(i)}
              data-testid={`pvc-session-card-${i + 1}`}
              className={`text-left rounded-2xl border p-5 transition-colors ${canOpen ? "bg-white border-slate-200 hover:border-[#4A6FA5]" : "bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed"}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#4A6FA5]/10 text-[#4A6FA5] shrink-0">
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">Session {i + 1} · {s.code}</p>
                    <p className="mt-0.5 font-heading text-xl text-slate-900 leading-snug">{s.title}</p>
                    <p className="text-sm text-slate-600 italic mt-0.5">{s.subtitle}</p>
                    <p className="text-xs text-slate-500 mt-2">Reading: {s.reading}</p>
                  </div>
                </div>
                <StatusPill status={status} />
              </div>
            </button>
          );
        })}
      </div>

      {allDone && (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={onFinish}
            data-testid="pvc-dashboard-finish"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
          >
            Continue to Final Journey <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  );
}

function StatusPill({ status }) {
  const tone = {
    "Completed":  "bg-emerald-50 text-emerald-800 border-emerald-200",
    "In Progress":"bg-amber-50 text-amber-800 border-amber-200",
    "Ready":      "bg-slate-50 text-slate-700 border-slate-200",
    "Locked":     "bg-slate-50 text-slate-500 border-slate-200",
  }[status] || "bg-slate-50 text-slate-700 border-slate-200";
  const testid = `pvc-status-${status.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${tone}`}>
      {status}
    </span>
  );
}

// ============================================================================
// Session — one screen at a time
// ============================================================================
function SessionScreen({ session, sectionIndex, section, onNext, onPrev, reflectionCache, saveReflection }) {
  const totalTeaching = session.sections.length;
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12" data-testid="pvc-session-screen">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-6">
        <p data-testid="pvc-session-crumb">Session {session.order} · {session.title}</p>
        <p data-testid="pvc-section-counter">{sectionIndex + 1} of {totalTeaching}</p>
      </div>

      <SectionRenderer
        section={section}
        session={session}
        reflectionCache={reflectionCache}
        saveReflection={saveReflection}
      />

      <div className="mt-10 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onPrev}
          data-testid="pvc-section-back"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="pvc-section-continue"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

// ============================================================================
// Section renderer — dispatches by `type`
// ============================================================================
function SectionRenderer({ section, session, reflectionCache, saveReflection }) {
  switch (section.type) {
    case "hear-voice":     return <HearVoiceSection section={section} />;
    case "text":           return <TextSection section={section} />;
    case "highlight":      return <HighlightSection section={section} />;
    case "two-col":        return <TwoColSection section={section} />;
    case "case":           return <CaseSection section={section} />;
    case "quick-check":    return <QuickCheckSection section={section} />;
    case "team-eyes":      return <TeamEyesSection section={section} />;
    case "menu":           return <MenuSection section={section} />;
    case "matters-plan":   return <MattersPlanSection section={section} />;
    case "pause-reflect":  return <ReflectionSection section={section} cache={reflectionCache} onSave={saveReflection} />;
    case "carry":          return <CarrySection section={section} />;
    case "empty-chair":    return <EmptyChairSection section={section} cache={reflectionCache} onSave={saveReflection} />;
    case "commitment":     return <CommitmentSection section={section} cache={reflectionCache} onSave={saveReflection} />;
    case "quote":          return <QuoteSection section={section} />;
    default:               return <TextSection section={{ heading: section.heading || "Section", body: section.body || "" }} />;
  }
}

// ---- section components ----
function HearVoiceSection({ section }) {
  return (
    <div data-testid="pvc-sec-hear-voice">
      <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">{section.eyebrow || "Hear the Voice"}</p>
      <h2 className="mt-2 font-heading text-3xl sm:text-4xl text-slate-900 leading-tight">{section.heading}</h2>
      <blockquote className="mt-6 border-l-4 border-[#4A6FA5] pl-5 py-3 text-lg text-slate-800 italic leading-relaxed bg-white/70 rounded-r-lg">
        {section.body}
      </blockquote>
    </div>
  );
}

function TextSection({ section }) {
  return (
    <div data-testid="pvc-sec-text">
      {section.heading && <h2 className="font-heading text-2xl sm:text-3xl text-slate-900 leading-tight">{section.heading}</h2>}
      <p className="mt-4 text-slate-700 text-lg leading-relaxed whitespace-pre-line">{section.body}</p>
    </div>
  );
}

function HighlightSection({ section }) {
  return (
    <div className="rounded-2xl border border-[#4A6FA5]/30 bg-[#4A6FA5]/5 p-6 sm:p-8" data-testid="pvc-sec-highlight">
      <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">Key Principle</p>
      <p className="mt-3 font-heading text-2xl sm:text-3xl text-slate-900 leading-tight italic">
        &ldquo;{section.body}&rdquo;
      </p>
    </div>
  );
}

function TwoColSection({ section }) {
  return (
    <div data-testid="pvc-sec-two-col">
      {section.heading && <h2 className="font-heading text-2xl sm:text-3xl text-slate-900 leading-tight">{section.heading}</h2>}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">{section.left_title}</p>
          <ol className="space-y-1.5 text-slate-700 text-sm">
            {section.left_items.map((it, i) => <li key={i} className="flex items-start gap-2"><span className="text-slate-400 mt-0.5">→</span><span>{it}</span></li>)}
          </ol>
        </div>
        <div className="rounded-xl border border-[#4A6FA5]/40 bg-[#4A6FA5]/5 p-5">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold mb-3">{section.right_title}</p>
          <ol className="space-y-1.5 text-slate-800 text-sm">
            {section.right_items.map((it, i) => <li key={i} className="flex items-start gap-2"><span className="text-[#4A6FA5] mt-0.5">→</span><span>{it}</span></li>)}
          </ol>
        </div>
      </div>
      {section.footer && <p className="mt-4 text-sm text-slate-600 italic">{section.footer}</p>}
    </div>
  );
}

function CaseSection({ section }) {
  const [revealed, setRevealed] = useState(false);
  const [choice, setChoice] = useState(null);
  const [choiceFeedback, setChoiceFeedback] = useState(null);
  return (
    <div data-testid="pvc-sec-case">
      {section.eyebrow && <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">{section.eyebrow}</p>}
      <h2 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 leading-tight">{section.heading}</h2>
      <p className="mt-5 text-slate-700 text-lg leading-relaxed">{section.scenario}</p>
      {section.before_prompt && (
        <p className="mt-4 text-sm text-slate-600 italic border-l-2 border-slate-300 pl-4">{section.before_prompt}</p>
      )}

      {!revealed ? (
        <button
          type="button"
          onClick={() => setRevealed(true)}
          data-testid="pvc-case-reveal"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-800"
        >
          {section.reveal_label || "LOOK BENEATH THE SURFACE"} <ArrowRight className="w-4 h-4" />
        </button>
      ) : (
        <>
          <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900 text-white p-5 sm:p-6" data-testid="pvc-case-reveal-body">
            <p className="text-[11px] uppercase tracking-[0.24em] text-amber-300 font-semibold mb-3">{section.reveal_label || "LOOK BENEATH THE SURFACE"}</p>
            <p className="text-slate-100 leading-relaxed">{section.reveal_body}</p>
          </div>

          {section.options && (
            <div className="mt-8" data-testid="pvc-case-practice">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Practice the conversation</p>
              <p className="font-heading text-lg text-slate-900 mb-4">{section.practice_question}</p>
              <div className="space-y-2">
                {section.options.map(opt => {
                  const isSelected = choice === opt.key;
                  const showFeedback = choiceFeedback === opt.key;
                  return (
                    <div key={opt.key}>
                      <button
                        type="button"
                        onClick={() => { setChoice(opt.key); setChoiceFeedback(opt.key); }}
                        data-testid={`pvc-case-option-${opt.key}`}
                        className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${isSelected ? "border-[#4A6FA5] bg-[#4A6FA5]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
                      >
                        <p className="text-slate-800 text-sm">{opt.label}</p>
                      </button>
                      {showFeedback && (
                        <FeedbackBanner kind={opt.feedback_kind} text={opt.feedback} correct={!!opt.correct} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FeedbackBanner({ kind, text, correct }) {
  const styles = {
    open:  "bg-emerald-50 border-emerald-200 text-emerald-900",
    close: "bg-slate-100 border-slate-300 text-slate-800",
    push:  "bg-amber-50 border-amber-200 text-amber-900",
  };
  const label = { open: "OPENS", close: "CLOSES", push: "PUSHES" }[kind] || "NOTE";
  return (
    <div className={`mt-2 rounded-lg border px-4 py-3 ${styles[kind] || styles.close}`} data-testid={`pvc-feedback-${kind}`}>
      <p className="text-[10px] uppercase tracking-[0.24em] font-bold">{label}{correct ? " · Best fit" : ""}</p>
      <p className="mt-1 text-sm leading-relaxed">{text}</p>
    </div>
  );
}

function QuickCheckSection({ section }) {
  const [choice, setChoice] = useState(null);
  return (
    <div data-testid="pvc-sec-quick-check">
      {section.eyebrow && <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">{section.eyebrow}</p>}
      <h2 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 leading-tight">{section.heading}</h2>
      <p className="mt-4 text-slate-700 text-lg leading-relaxed">{section.prompt}</p>
      <div className="mt-6 space-y-2">
        {section.options.map(opt => {
          const active = choice === opt.key;
          return (
            <div key={opt.key}>
              <button
                type="button"
                onClick={() => setChoice(opt.key)}
                data-testid={`pvc-quick-option-${opt.key}`}
                className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${active ? "border-[#4A6FA5] bg-[#4A6FA5]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <p className="text-slate-800 text-sm">{opt.label}</p>
              </button>
              {active && <FeedbackBanner kind={opt.feedback_kind} text={opt.feedback} correct={!!opt.correct} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamEyesSection({ section }) {
  return (
    <div data-testid="pvc-sec-team-eyes">
      <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">Through the Team&rsquo;s Eyes</p>
      <h2 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 leading-tight">{section.heading}</h2>
      {section.intro && <p className="mt-4 text-slate-700 leading-relaxed">{section.intro}</p>}
      <ul className="mt-6 space-y-3">
        {section.perspectives.map(p => (
          <li key={p.role} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">{p.role}</p>
            <p className="mt-1 text-sm text-slate-700 leading-relaxed">{p.lens}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MenuSection({ section }) {
  return (
    <div data-testid="pvc-sec-menu">
      <h2 className="font-heading text-2xl sm:text-3xl text-slate-900 leading-tight">{section.heading}</h2>
      {section.intro && <p className="mt-4 text-slate-700 leading-relaxed">{section.intro}</p>}
      <ul className="mt-6 grid sm:grid-cols-2 gap-2">
        {section.items.map(it => (
          <li key={it} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5" /> <span>{it}</span>
          </li>
        ))}
      </ul>
      {section.footer && <p className="mt-4 text-sm text-slate-600 italic">{section.footer}</p>}
    </div>
  );
}

function MattersPlanSection({ section }) {
  return (
    <div data-testid="pvc-sec-matters-plan">
      {section.eyebrow && <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">{section.eyebrow}</p>}
      <h2 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 leading-tight">{section.heading}</h2>
      {section.intro && <p className="mt-4 text-slate-700 leading-relaxed">{section.intro}</p>}
      <ul className="mt-6 space-y-2">
        {section.items.map(it => (
          <li key={it} className="flex items-start gap-3 text-slate-700">
            <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[#4A6FA5]" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReflectionSection({ section, cache, onSave }) {
  const initial = cache[section.input_id] || "";
  const [val, setVal] = useState(initial);
  useEffect(() => { setVal(cache[section.input_id] || ""); }, [section.input_id]); // eslint-disable-line
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-6 sm:p-8" data-testid="pvc-sec-reflect">
      <div className="flex items-center gap-2">
        <p className="text-[11px] uppercase tracking-[0.24em] text-amber-800 font-semibold">{section.eyebrow || "Pause & Reflect"}</p>
        <span className="text-[10px] uppercase tracking-[0.18em] text-amber-800/70 font-semibold">Not scored</span>
      </div>
      <h2 className="mt-2 font-heading text-2xl text-slate-900 leading-tight">{section.heading}</h2>
      <p className="mt-4 text-slate-700 leading-relaxed">{section.prompt}</p>
      <textarea
        id={`ref-${section.input_id}`}
        data-testid={`pvc-reflect-input-${section.input_id}`}
        rows={5}
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => onSave(section.input_id, val)}
        placeholder="Write your reflection…"
        aria-label={section.heading}
        className="mt-4 w-full rounded-lg border border-amber-200 bg-white p-3 text-slate-800 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-400"
      />
      <p className="mt-2 text-xs text-amber-800/80">Saved to your preview state on blur.</p>
    </div>
  );
}

function CarrySection({ section }) {
  return (
    <div className="rounded-2xl border border-[#4A6FA5]/30 bg-white p-6 sm:p-8" data-testid="pvc-sec-carry">
      {section.eyebrow && <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">{section.eyebrow}</p>}
      <h2 className="mt-2 font-heading text-2xl text-slate-900 leading-tight">{section.heading}</h2>
      {section.body && <p className="mt-4 text-slate-700 leading-relaxed">{section.body}</p>}
      {section.items && (
        <ul className="mt-4 space-y-2">
          {section.items.map(it => (
            <li key={it} className="flex items-start gap-3 text-slate-800">
              <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#4A6FA5]/10 text-[#4A6FA5] shrink-0"><ArrowRight className="w-3 h-3" /></span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyChairSection({ section, cache, onSave }) {
  const initial = cache[section.input_id] || "";
  const [val, setVal] = useState(initial);
  useEffect(() => { setVal(cache[section.input_id] || ""); }, [section.input_id]); // eslint-disable-line
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 text-white p-6 sm:p-8" data-testid="pvc-sec-empty-chair">
      {section.eyebrow && <p className="text-[11px] uppercase tracking-[0.24em] text-amber-300 font-semibold">{section.eyebrow}</p>}
      <h2 className="mt-2 font-heading text-2xl sm:text-3xl text-white leading-tight">{section.heading}</h2>
      <p className="mt-4 text-slate-200 leading-relaxed">{section.intro}</p>
      <ul className="mt-6 space-y-2">
        {section.questions.map(q => (
          <li key={q} className="flex items-start gap-3 text-slate-100">
            <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-300" />
            <span>{q}</span>
          </li>
        ))}
      </ul>
      <p className="mt-6 font-heading text-lg text-white">{section.closing_prompt}</p>
      <textarea
        data-testid={`pvc-empty-chair-input`}
        rows={4}
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => onSave(section.input_id, val)}
        placeholder="What would we do differently for the next patient?"
        className="mt-4 w-full rounded-lg border border-slate-700 bg-slate-800 p-3 text-slate-100 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-300"
      />
    </div>
  );
}

function CommitmentSection({ section, cache, onSave }) {
  const [main, setMain] = useState(cache[section.input_id] || "");
  const [followup, setFollowup] = useState(cache[section.followup_input_id] || "");
  useEffect(() => {
    setMain(cache[section.input_id] || "");
    setFollowup(cache[section.followup_input_id] || "");
  }, [section.input_id, section.followup_input_id]); // eslint-disable-line
  return (
    <div className="rounded-2xl border border-[#4A6FA5] bg-[#4A6FA5]/5 p-6 sm:p-8" data-testid="pvc-sec-commitment">
      {section.eyebrow && <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">{section.eyebrow}</p>}
      <h2 className="mt-2 font-heading text-3xl text-slate-900 leading-tight">{section.heading}</h2>
      <p className="mt-4 text-slate-700 leading-relaxed">{section.intro}</p>
      <div className="mt-6 rounded-xl border border-white/60 bg-white p-4">
        <p className="font-heading text-lg text-slate-800 italic">{section.stem}</p>
        <textarea
          data-testid="pvc-commitment-input"
          rows={3}
          value={main}
          onChange={e => setMain(e.target.value)}
          onBlur={() => onSave(section.input_id, main)}
          placeholder="Because of this course, I will…"
          className="mt-3 w-full rounded-lg border border-slate-200 p-3 text-slate-800 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
        />
      </div>
      <p className="mt-5 text-sm text-slate-700 font-semibold">{section.followup_prompt}</p>
      <textarea
        data-testid="pvc-commitment-followup-input"
        rows={3}
        value={followup}
        onChange={e => setFollowup(e.target.value)}
        onBlur={() => onSave(section.followup_input_id, followup)}
        placeholder="How will this commitment help you keep the patient's voice present in your care?"
        className="mt-3 w-full rounded-lg border border-slate-200 bg-white p-3 text-slate-800 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
      />
    </div>
  );
}

function QuoteSection({ section }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="pvc-sec-quote">
      <Quote className="w-6 h-6 text-[#4A6FA5] mx-auto" strokeWidth={1.75} />
      <p className="mt-4 font-heading text-2xl sm:text-3xl text-slate-800 italic leading-tight">
        &ldquo;{section.body}&rdquo;
      </p>
      {section.attribution && (
        <p className="mt-4 text-sm text-slate-500 uppercase tracking-[0.14em] font-semibold">— {section.attribution}</p>
      )}
    </div>
  );
}

// ============================================================================
// Knowledge check
// ============================================================================
function CheckScreen({ session, question, index, total, onSubmit, onPrev, instructions }) {
  const [selected, setSelected] = useState(null);
  useEffect(() => { setSelected(null); }, [question.id]);
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12" data-testid="pvc-check-screen">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-6">
        <p>Session {session.order} · Check Your Understanding</p>
        <p data-testid="pvc-check-counter">Question {index + 1} of {total}</p>
      </div>
      <p className="text-xs text-slate-500 mb-4">{instructions}</p>
      <h2 className="font-heading text-2xl sm:text-3xl text-slate-900 leading-tight" data-testid="pvc-check-prompt">{question.prompt}</h2>
      <div className="mt-6 space-y-2">
        {question.choices.map(c => {
          const active = selected === c.k;
          return (
            <button
              key={c.k}
              type="button"
              onClick={() => setSelected(c.k)}
              data-testid={`pvc-check-option-${c.k}`}
              className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${active ? "border-[#4A6FA5] bg-[#4A6FA5]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full border ${active ? "border-[#4A6FA5] bg-[#4A6FA5] text-white" : "border-slate-300 text-slate-500"}`}>
                  <span className="text-[11px] font-semibold uppercase">{c.k}</span>
                </span>
                <span className="text-slate-800 text-sm">{c.t}</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-8 flex items-center justify-between gap-4">
        <button type="button" onClick={onPrev} data-testid="pvc-check-back" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          type="button"
          disabled={!selected}
          onClick={() => onSubmit(selected)}
          data-testid="pvc-check-submit"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium ${selected ? "bg-[#4A6FA5] text-white hover:bg-[#3B597B]" : "bg-slate-200 text-slate-500 cursor-not-allowed"}`}
        >
          Submit Answer <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

function CheckResultScreen({ question, result, index, total, onNext }) {
  const correctChoice = question.choices.find(c => c.k === question.answer);
  const isCorrect = !!result?.correct;
  const isLast = index + 1 >= total;
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12" data-testid="pvc-check-result">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-6">
        <p>Result · Question {index + 1} of {total}</p>
      </div>
      <div className={`rounded-2xl border p-6 sm:p-8 ${isCorrect ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
        <p className={`text-[11px] uppercase tracking-[0.24em] font-semibold ${isCorrect ? "text-emerald-800" : "text-amber-800"}`} data-testid="pvc-check-verdict">
          {isCorrect ? "Correct" : "Review this one"}
        </p>
        <h2 className="mt-2 font-heading text-2xl text-slate-900 leading-tight">{question.prompt}</h2>
        <p className="mt-4 text-sm text-slate-700">
          <span className="font-semibold">Correct answer:</span> ({question.answer.toUpperCase()}) {correctChoice?.t}
        </p>
        <p className="mt-3 text-sm text-slate-700 leading-relaxed">{question.explanation}</p>
      </div>
      <div className="mt-8 flex items-center justify-end">
        <button type="button" onClick={onNext} data-testid="pvc-check-next" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
          {isLast ? "Continue" : "Next Question"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

// ============================================================================
// Session complete
// ============================================================================
function SessionCompleteScreen({ session, onContinue }) {
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="pvc-session-complete">
      <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-700 font-semibold">Session Complete</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl text-slate-900 leading-tight">Session {session.order}: {session.title}</h1>
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">Completion statement</p>
        <p className="mt-3 font-heading text-xl text-slate-800 leading-relaxed italic">
          {session.completion_statement}
        </p>
      </div>
      <div className="mt-6 rounded-2xl border border-[#4A6FA5]/30 bg-[#4A6FA5]/5 p-6 sm:p-8">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">Carry It Into Care</p>
        <p className="mt-3 text-slate-800 leading-relaxed">{session.carry_forward}</p>
      </div>
      <div className="mt-10 flex justify-end">
        <button type="button" onClick={onContinue} data-testid="pvc-session-complete-continue" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
          Complete Session <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

// ============================================================================
// Between-session transition
// ============================================================================
function TransitionScreen({ from, onBegin }) {
  const t = from.transition_next;
  if (!t) return null;
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 md:py-24 text-center" data-testid="pvc-transition">
      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-semibold">You have learned to</p>
      <h2 className="mt-2 font-heading text-4xl sm:text-5xl text-slate-900 leading-tight">{t.you_learned}</h2>

      <div className="my-10 flex items-center justify-center gap-3 text-slate-400">
        <div className="h-px w-12 bg-slate-300" />
        <span className="text-xs uppercase tracking-[0.24em] font-semibold">Next</span>
        <div className="h-px w-12 bg-slate-300" />
      </div>

      <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">{t.next_code}</p>
      <h3 className="mt-2 font-heading text-3xl sm:text-4xl text-slate-900 leading-tight">{t.next_title}</h3>
      <p className="mt-4 text-slate-600 italic max-w-xl mx-auto leading-relaxed">{t.teaser}</p>

      <button type="button" onClick={onBegin} data-testid="pvc-transition-begin" className="mt-10 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
        Begin Session <ArrowRight className="w-4 h-4" />
      </button>
    </section>
  );
}

// ============================================================================
// Final journey + Before-you-finish + Complete
// ============================================================================
function FinalJourneyScreen({ course, onNext }) {
  const fj = course.final_journey;
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 md:py-20 text-center" data-testid="pvc-final-journey">
      <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">From Diagnosis to Goodbye</p>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-slate-900 leading-tight">The Patient&rsquo;s Voice in Care</h1>

      <ol className="mt-10 space-y-3" data-testid="pvc-final-journey-list">
        {fj.progression.map((step, i) => (
          <li key={step} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3 justify-center">
            <span className="text-xs uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">Step {i + 1}</span>
            <span className="text-slate-800 font-medium">{step}</span>
          </li>
        ))}
      </ol>

      <blockquote className="mt-12 border-l-4 border-[#4A6FA5] pl-6 text-left max-w-xl mx-auto">
        <p className="font-heading text-2xl text-slate-800 italic leading-tight">&ldquo;{fj.closing_quote}&rdquo;</p>
        <p className="mt-3 text-sm text-slate-500 uppercase tracking-[0.14em] font-semibold">— {fj.closing_attribution}</p>
      </blockquote>

      <p className="mt-10 text-slate-700 leading-relaxed">{fj.closing_thanks}</p>

      <button type="button" onClick={onNext} data-testid="pvc-final-journey-continue" className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
        Continue <ArrowRight className="w-4 h-4" />
      </button>
    </section>
  );
}

function BeforeYouFinishScreen({ commitment, followup, onFinish }) {
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 md:py-20" data-testid="pvc-before-finish">
      <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">Before You Finish…</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl text-slate-900 leading-tight">Think about the next patient whose room you will enter.</h1>
      <p className="mt-6 text-slate-700 text-lg leading-relaxed">Will they experience you differently because you completed this course?</p>
      <p className="mt-4 text-slate-700 text-lg leading-relaxed">What will you do differently?</p>

      {(commitment || followup) && (
        <div className="mt-10 rounded-2xl border border-[#4A6FA5]/30 bg-[#4A6FA5]/5 p-6" data-testid="pvc-before-finish-commitment">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">Your Patient Voice Commitment</p>
          {commitment && <p className="mt-3 text-slate-800 italic leading-relaxed">&ldquo;Because of this course, I will {commitment}&rdquo;</p>}
          {followup && <p className="mt-3 text-slate-700 leading-relaxed">{followup}</p>}
        </div>
      )}

      <div className="mt-10 flex justify-end">
        <button type="button" onClick={onFinish} data-testid="pvc-before-finish-continue" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
          Finish Course <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

function CourseCompleteScreen({ course, onDashboard, onReset }) {
  const nav = useNavigate();
  const s = previewStore.getState();
  const commitment = (s.progress.case_study_responses || {})["pvc-commitment"] || "";
  const commitmentFollowup = (s.progress.case_study_responses || {})["pvc-commitment-followup"] || "";
  // Accurate metric: sum of knowledge-check questions the learner has actually
  // answered. Each session has exactly 3 KC questions. Session completion is
  // marked when the last KC answer is submitted, so answered = 3 × completed sessions.
  const completions = s.progress.section_completions || {};
  const sessionsDone = course.sessions.filter(x => completions[x.id]).length;
  const totalKcQuestions = course.sessions.reduce(
    (n, x) => n + (x.knowledge_check?.questions?.length || 0), 0);
  const kcAnswered = course.sessions.reduce(
    (n, x) => n + (completions[x.id] ? (x.knowledge_check?.questions?.length || 0) : 0), 0);
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 md:py-20 text-center" data-testid="pvc-course-complete">
      <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" strokeWidth={1.5} />
      <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-emerald-700 font-semibold">Course Complete ✓</p>
      <h1 className="mt-2 font-heading text-4xl sm:text-5xl tracking-tight text-slate-900 leading-tight">{course.title}</h1>
      <p className="mt-3 font-heading text-xl text-slate-700">{course.subtitle}</p>

      <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 text-left" data-testid="pvc-completion-record">
        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Completion record (preview only)</p>
        <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div><dt className="text-slate-500">Learner</dt><dd className="text-slate-800 font-medium">Admin Preview</dd></div>
          <div><dt className="text-slate-500">Course ID</dt><dd className="text-slate-800 font-medium">{course.code}</dd></div>
          <div><dt className="text-slate-500">Course title</dt><dd className="text-slate-800 font-medium">{course.title}</dd></div>
          <div><dt className="text-slate-500">Completion date</dt><dd className="text-slate-800 font-medium">{new Date().toISOString().slice(0, 10)}</dd></div>
          <div><dt className="text-slate-500">Sessions completed</dt><dd className="text-slate-800 font-medium" data-testid="pvc-completion-sessions">{sessionsDone} of {course.sessions.length}</dd></div>
          <div><dt className="text-slate-500">Knowledge-check questions answered</dt><dd className="text-slate-800 font-medium" data-testid="pvc-completion-kc">{kcAnswered} of {totalKcQuestions}</dd></div>
          <div className="sm:col-span-2">
            <dt className="text-slate-500">Patient Voice Commitment</dt>
            {commitment ? (
              <dd className="text-slate-800" data-testid="pvc-completion-commitment">
                <p className="italic">&ldquo;Because of this course, I will {commitment}&rdquo;</p>
                {commitmentFollowup && <p className="mt-1 text-slate-700 not-italic">{commitmentFollowup}</p>}
              </dd>
            ) : (
              <dd className="text-slate-500" data-testid="pvc-completion-commitment-empty">— (not entered)</dd>
            )}
          </div>
          <div className="sm:col-span-2"><dt className="text-slate-500">Credential</dt><dd className="text-slate-800 font-medium">Certificate of Completion</dd></div>
        </dl>
        <p className="mt-6 text-xs text-slate-500 italic">
          Certificate architecture is in place; production certificate issuance is not yet enabled for PVC-001. No CE, CEU, CME, or nursing contact hours are offered at this time.
        </p>
      </div>

      <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
        <button type="button" onClick={onDashboard} data-testid="pvc-complete-dashboard" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white">
          Return to Dashboard
        </button>
        <button
          type="button"
          onClick={() => nav("/academy/portal/pvc-001/certificate-preview")}
          data-testid="pvc-complete-cert-preview"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#4A6FA5] bg-white text-[#4A6FA5] font-medium hover:bg-[#F0F4F8]"
        >
          Preview Certificate
        </button>
        <button type="button" onClick={onReset} data-testid="pvc-complete-reset" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
          <RotateCcw className="w-4 h-4" /> Reset & Preview Again
        </button>
      </div>
      <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold" data-testid="pvc-complete-cert-note">
        Certificate preview is admin-only · not a production credential
      </p>
    </section>
  );
}

// ============================================================================
// Utilities
// ============================================================================
function Card({ title, children, testid, tone }) {
  const bg = tone === "muted" ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-2xl border p-6 ${bg}`}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">{title}</p>
      {children}
    </div>
  );
}

function FullscreenMessage({ title, body, action }) {
  return (
    <div className="min-h-screen bg-[#FDFCF6] flex items-center justify-center px-6" data-testid="pvc-fullscreen-msg">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-2xl text-slate-900">{title}</h1>
        {body && <p className="mt-3 text-slate-600 leading-relaxed">{body}</p>}
        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
}
